import { DocumentNode } from 'graphql';
import { Star } from 'react-feather';
import { useEffect, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import shuffle from 'lodash/shuffle';

import { ReviewAspectType, SaveReviewMutation as SaveReview } from '../../__generated__/graphql';

import { useNotifier } from '../../common/Notifications/NotificationProvider';
import DeleteReviewMutation from './DeleteReviewMutation';
import SaveReviewMutation from './SaveReviewMutation';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';

const maxAspects = 3;

const reviewAspectTypes = shuffle([
  ReviewAspectType.course,
  ReviewAspectType.location,
  ReviewAspectType.atmosphere,
  ReviewAspectType.physical_challenge,
  ReviewAspectType.race_experience,
  ReviewAspectType.organization,
  ReviewAspectType.unique_experience,
]);

export interface RegistrationSummary {
  id: string;
  can_add_review: boolean;
  review?: {
    id: string;
    rating: number;
    aspects: ReviewAspectType[];
    comment?: string | null;
    created_at: string;
  } | null;
  participant: {
    id: string;
    first_name: string;
    full_name: string;
  };
  event: {
    id: string;
    title: string;
    start_date: string;
    end_date: string;
    checkout_url: string;
    active: boolean;
    review_average_rating: number;
    reviews: {
      total: number;
    };
    logo?: {
      id: string;
      url: string;
    } | null;
    project: {
      timezone: string;
    };
  };
}

export interface ReviewFormProps {
  summary: RegistrationSummary;
  rating?: number;
  editable?: boolean;
  onSuccess?: (review: SaveReview['saveReview']) => unknown;
  onDelete?: () => unknown;
  refetchQueries?: DocumentNode[];
}

export const ReviewForm = ({
  summary, rating: ratingProp, onSuccess, onDelete, refetchQueries,
}: ReviewFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [rating, setRating] = useState(ratingProp);
  const ratings = [1, 2, 3, 4, 5];

  const form = useForm({
    values: {
      rating: summary.review?.rating || 0,
      comment: summary.review?.comment || '',
      aspects: summary.review?.aspects || [],
    },
  });

  useEffect(() => {
    if (ratingProp) {
      form.set('rating', ratingProp);
    }
  }, [ratingProp]);

  const [saveReview, { error, loading }] = useMutation(SaveReviewMutation, {
    variables: {
      input: {
        event_id: summary.event.id,
        ...form.values,
      },
    },
    onCompleted: (data) => {
      onSuccess?.(data.saveReview);
      notifier.success(t('review.review_posted'));
    },
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const [deleteReview, { loading: deleting }] = useMutation(DeleteReviewMutation, {
    variables: {
      id: summary.review?.id,
    },
    onCompleted: () => {
      onDelete?.();
      notifier.success(t('review.review_deleted'));
    },
    refetchQueries,
    awaitRefetchQueries: true,
  });

  return (
    <UI.Form onSubmit={() => saveReview()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mr: 6 }}>
            {t('review.rate_your_experience_at_event', { event: summary.event.title })}
          </UI.H4>

        </UI.Div>

        <UI.HR />
        <UI.Div>
          <UI.FlexContainer sc={{ alignItems: 'center', gap: 0.5 }}>
            <UI.ProfilePic sc={{
              fit: 'cover', background: 'secondary', size: 34, inlineBlock: true, my: -0.25,
            }}
            >
              <span>
                <span>
                  {summary.participant.full_name.charAt(0)}
                </span>
              </span>
            </UI.ProfilePic>
            <UI.Div>
              <UI.Div sc={{ strong: true }}>
                {summary.participant.full_name}
              </UI.Div>
              {t('review.posting_publicly_note')}
            </UI.Div>
          </UI.FlexContainer>
        </UI.Div>
        <UI.HR />

        <UI.ServerErrors error={error} />

        <UI.InputGroup>
          <UI.InputLabel>
            {t('review.overall_rating')}
          </UI.InputLabel>
          <UI.FlexContainer>
            <UI.Card sc={{ outline: 'gray.200', spacing: 'xs', flex: { alignItems: 'center' } }}>
              {ratings.map((value) => (
                <UI.Button
                  sc={{
                    brand: 'primary.50',
                    blank: true,
                    size: 'lg',
                    color: value <= rating ? 'primary' : 'gray.800',
                    px: 1.5,
                    py: 1,
                  }}
                  onMouseEnter={() => setRating(value)}
                  onMouseLeave={() => setRating(form.values.rating)}
                  onFocus={() => setRating(value)}
                  onBlur={() => setRating(form.values.rating)}
                  onClick={(event) => {
                    form.set('rating', value);
                    event.currentTarget.blur();
                  }}
                  title={t('review.rating_label', { rating: value })}
                  disabled={!summary.can_add_review}
                  key={value}
                >
                  <UI.Icon sc={{ size: 2, color: value <= rating ? 'primary' : undefined }}>
                    <Star fill={value <= rating ? 'currentColor' : 'none'} strokeWidth={1.5} />
                  </UI.Icon>
                </UI.Button>
              ))}
              <UI.Div sc={{ background: 'gray.200', mx: 1 }} style={{ width: '1px', height: '100%' }} />
              <UI.Div sc={{ fontSize: 4, px: 1, textAlign: 'center' }} style={{ minWidth: 65 }}>
                <UI.Strong>
                  {rating}
                </UI.Strong>
                <UI.Span sc={{ fontWeight: 300 }}>
                  {' / 5'}
                </UI.Span>
              </UI.Div>
            </UI.Card>
          </UI.FlexContainer>
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel>
            {t('review.your_favorite_aspects')}
            {' '}
            <UI.Badge
              sc={{ brand: 'gray.150', fontWeight: 500 }}
            >
              {form.values.aspects.length}
              {' / '}
              {maxAspects}
            </UI.Badge>
          </UI.InputLabel>
          <UI.FlexContainer sc={{ gap: 0.25, wrap: 'wrap' }}>
            {reviewAspectTypes.map((type) => (
              <UI.Checkbox
                {...form.checklist('aspects', type)}
                sc={{ box: true }}
                disabled={(form.values.aspects.length >= 3 && !form.values.aspects.includes(type))
                  || !summary.can_add_review}
                key={type}
              >
                {t(`common:review_aspect_types.${type}`)}
              </UI.Checkbox>
            ))}
          </UI.FlexContainer>
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="ReviewComment">
            {t('review.comment')}
          </UI.InputLabel>
          <UI.TextArea
            {...form.textarea('comment')}
            placeholder={t('review.comment_label')}
            aria-label={t('review.comment_label')}
            id="ReviewComment"
            disabled={!summary.can_add_review}
          />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={summary.review ? t('review.update_review') : t('review.post_review')}
          deleteLabel={summary.review ? t('common:forms.delete') : undefined}
          onDelete={summary.review ? () => deleteReview() : undefined}
          loading={loading || deleting}
          enabled={form.changed() && summary.can_add_review}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseReviewModalProps {
  summary: RegistrationSummary;
  onSuccess?: (review: SaveReview['saveReview']) => unknown;
  onDelete?: () => unknown;
  refetchQueries?: DocumentNode[];
}

const useReviewModal = ({ summary, onSuccess, onDelete, refetchQueries }: UseReviewModalProps) => {
  const [rating, setRating] = useState(0);

  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = (review: SaveReview['saveReview']) => {
      onSuccess?.(review);
      closeModal();
    };

    const handleDelete = () => {
      onDelete?.();
      closeModal();
    };

    return (
      <UI.Modal close={closeModal} maxWidth={600}>
        <ReviewForm
          summary={summary}
          rating={rating}
          onSuccess={handleSuccess}
          onDelete={handleDelete}
          refetchQueries={refetchQueries}
        />
      </UI.Modal>
    );
  }, [summary.event, summary.review, rating, refetchQueries]);

  const startReview = (rating: number) => {
    setRating(rating);
    openModal();
  };

  return [startReview, closeModal] as const;
};

export default useReviewModal;
